import React, { useEffect } from 'react';
import { getAppTitle } from 'api/Utils';
import { LastMenuItemContainer, Logo, LogoContent } from './styles';
import messages from './Messages';
import Menu from 'components/Menu';
import SideBar from 'components/Sidebar';
import { IProviders } from 'store/providers/providerTypes';
import { smartNumericCompare } from 'utils/string';
import {
  ICompany,
  ICompanyProviders,
  TProjects,
} from 'store/companies/companiesTypes';
import { useTranslation } from 'react-i18next';
import LocalIcon from '../../../ui/LocalIcon';
import { black, lightGray } from '../../../utils/style/colors';
import i18n from '../../../i18n';
import { useTour } from '@reactour/tour';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { IUserFlag } from '../../../store/thisUser/thisUserSlice';
import { addTourStep } from '../../../context/Tour/TourWrapper';
import Marquee from '../../../components/Marquee/Marquee';
import UserMixpanel from 'utils/mixpanel/MixpanelClass';

const appTitle = getAppTitle();

const { SubMenu } = Menu;
const getInitialOpenStatusSubMenu = (
  basePath: string,
  projectIds: number[]
): boolean => {
  if (window.location.pathname.startsWith(basePath)) {
    const currentProjectId = window.location.pathname
      .replace(basePath, '')
      .split('/')[0];
    if (currentProjectId) {
      return projectIds.indexOf(Number(currentProjectId)) !== -1;
    }
    return false;
  }
  return false;
};

type TaskSidebarProps = {
  collapsed: boolean;
  companyId: number;
  companyName: string;
  hasCompany: boolean;
  hasPDP: boolean;
  hasUnreadMessage: boolean;
  onCollapse: () => void;
  projects: TProjects;
  companyAdmin: boolean;
  supportOrOwner: boolean;
  allProviders: IProviders;
  getDocumentsInFolder: (folderId: number, providerId: number) => Promise<any>;
  providers: ICompanyProviders;
  unreadChatMessage: boolean;
  onChangeGeneralSearchStatus: (status: boolean) => void;
  company: ICompany;
};

const TaskSidebar: React.FC<TaskSidebarProps> = ({
  allProviders,
  collapsed,
  companyAdmin,
  companyId,
  companyName,
  getDocumentsInFolder,
  hasCompany,
  hasPDP,
  onCollapse,
  projects,
  providers,
  unreadChatMessage,
  onChangeGeneralSearchStatus,
  company,
}: TaskSidebarProps) => {
  const { t } = useTranslation('mainNavBar');

  const thisUserId = useSelector((state: RootState) => state.thisUser.userId);

  const generalSearchList = [
    {
      selector: '.generalsearch',
      content: `${i18n.t('generalSearchTutorial', { ns: 'general' })}`,
      category: 'generalsearch',
    },
  ];
  const tourFlags = useSelector(
    (state: RootState) => state.thisUser.userFlags?.['tour']
  );

  const { setSteps } = useTour();

  useEffect(() => {
    if (
      !tourFlags?.find(
        (flag: IUserFlag) => flag.flagName === 'generalsearch'
      ) &&
      !collapsed &&
      thisUserId
    ) {
      addTourStep(setSteps, generalSearchList || []);
    }
  }, [collapsed]);

  const companyProjects = Object.values(projects || {}).filter(
    (project) => project.companyId === Number(companyId)
  );
  const taggedProjects = companyProjects.reduce(
    (list: { [key: string]: number[] }, project) => {
      if (project.archived) return list;
      const tagsOfProject = project.tags;
      if (tagsOfProject === null || tagsOfProject.length === 0) {
        list[t('others')] = list[t('others')] || [];
        list[t('others')].push(project.id);
      } else {
        tagsOfProject.forEach((tag: string) => {
          list[tag] = list[tag] || [];
          list[tag].push(project.id);
        });
      }
      return list;
    },
    {}
  );

  useEffect(() => {
    if (!providers) return;
    Object.values(providers).forEach((p) => {
      const provider = allProviders.map[p!.id].data;
      if (provider?.useVirtualSpaces) {
        const providerId = p!.id;
        const rootFolderId = provider!.rootDocument!.id;
        const items = Object.values(
          allProviders.items[providerId].items
        ).filter(
          (item) =>
            item.data?.dadId === rootFolderId && item.data.type === 'folder'
        );
        if (items.length <= 1) {
          getDocumentsInFolder(rootFolderId, providerId);
        }
      }
    });
  }, [providers]);

  const isAdminInAnyProject = Object.values(projects || {}).reduce(
    (current: boolean, item) => {
      if (item.myMembership?.admin) return true;
      return current;
    },
    false
  );

  const companyTitle = hasCompany ? (collapsed ? '' : companyName) : appTitle;

  return (
    <SideBar
      collapsed={collapsed}
      onCollapse={onCollapse}
      companyId={companyId}
      key={`task-sidebar-${companyId}`}
    >
      <Logo collapsed={collapsed}>
        <LogoContent>
          <Marquee text={companyTitle} />
        </LogoContent>

        {thisUserId && (
          <div
            style={{
              borderRadius: '10px',
              backgroundColor: !collapsed ? lightGray : undefined,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            onClick={() => {
              onChangeGeneralSearchStatus(true);
            }}
            className="generalsearch"
          >
            <LocalIcon
              iconName={'search-normal.svg'}
              color={`${black}`}
              isLink={true}
              type={'sm'}
              style={{ padding: '0 10px' }}
            />

            {!collapsed && t('generalSearch')}
          </div>
        )}
      </Logo>
      {hasCompany ? (
        <div
          style={{
            overflow: 'auto',
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
          }}
          key={`home-provider-list-${companyId}`}
        >
          <Menu key="home-provider-list">
            <Menu.Item
              iconType="home.svg"
              key="home"
              label={t('home')}
              exact={false}
              path={`/t/${companyId}/home`}
            />
            <Menu.Divider />

            {hasPDP && (
              <>
                {company.pdpPanels.map((el) => (
                  <Menu.Item
                    iconType={(el.properties?.icon as any) || 'teacher.svg'}
                    key={`company-${el.id}`}
                    label={el.properties?.name || t('pdpPanel')}
                    path={`/t/${companyId}/pdp/${el.id}/terms`}
                    onClick={() => {
                      const mx = UserMixpanel.getInstance(Number(thisUserId));
                      mx.handleAddNumberOfEnteredPDP();
                      if (!collapsed) onCollapse();
                    }}
                  />
                ))}

                <Menu.Divider />
              </>
            )}
            {providers &&
              Object.keys(providers || {}).length > 0 &&
              Object.values(providers)
                .sort((a, b) => a.id - b.id)
                .map((p) => {
                  const provider = allProviders.map[p.id].data;
                  if (provider?.useVirtualSpaces) {
                    const providerId = p!.id;
                    const rootFolderId = provider!.rootDocument!.id;
                    const items =
                      Object.values(
                        allProviders.items[providerId].items
                      ).filter((item) => {
                        if (!item) {
                          return false;
                        }
                        return (
                          item.data.dadId === rootFolderId &&
                          item.data.type === 'folder' &&
                          (!(
                            item.data.tags.some(
                              (it) => 'conf_hidden' === it.text.toLowerCase()
                            ) || item.data.hidden
                          ) ||
                            item.data.editable)
                        );
                      }) ?? [];

                    const elements = (
                      <>
                        {Object.values(items)
                          .sort((item) => {
                            return item.data.order;
                          })
                          .map((item) => {
                            const itemId = item.data.id;
                            const itemTitle = item.data.name;
                            return (
                              // its icon type need to change
                              // this is just a fake icon type
                              <Menu.Item
                                iconType="zip.svg"
                                key={itemId}
                                label={itemTitle!}
                                path={`/content/${providerId}/f/${itemId}`}
                              />
                            );
                          })}
                      </>
                    );
                    return elements;
                  } else if (
                    !(
                      allProviders.map[p!.id].data?.configFlag.hidden === 'true'
                    ) ||
                    companyAdmin
                  )
                    return (
                      <>
                        <Menu.Item
                          iconType="zip.svg"
                          key={p!.id}
                          label={messages.providerContent(p!.name)}
                          path={`/content/${p!.id}`}
                        />
                        {/* {supportOrOwner && (
                          <>
                            <Menu.Item
                              iconType="tag.svg"
                              key={p!.id + 'tag'}
                              label={messages.tags}
                              path={`/content/${p!.id}/tags`}
                            />
                            <Menu.Item
                              iconType="setting.svg"
                              key={p!.id + 'settings'}
                              label={messages.settings}
                              path={`/content/${p!.id}/settings`}
                            />
                          </>
                        )}*/}
                      </>
                    );
                  else return <></>;
                })}
            {companyName && !!Object.keys(taggedProjects).length && (
              <>
                <Menu.Divider />
                <Menu.Item
                  iconType="tasks.svg"
                  key={companyId}
                  label={t('alltasks')}
                  path={`/t/${companyId}/task`}
                />
              </>
            )}

            {Object.keys(taggedProjects)
              .sort((a, b) =>
                a === t('others')
                  ? 1
                  : b === t('others')
                    ? -1
                    : a.localeCompare(b)
              )
              .map((taggedProjectName) => {
                const initialOpenStatus = getInitialOpenStatusSubMenu(
                  `/t/${companyId}/project/`,
                  taggedProjects[taggedProjectName]
                );
                return (
                  <SubMenu
                    iconType="document-normal.svg"
                    initialOpenStatus={initialOpenStatus}
                    key={taggedProjectName}
                    label={taggedProjectName}
                  >
                    {taggedProjects[taggedProjectName]
                      .sort((a, b) => {
                        return smartNumericCompare(
                          projects[a].name,
                          projects[b].name
                        );
                      })
                      .map((projectId: number) => (
                        <SubMenu.Item
                          key={projectId}
                          iconType="task.svg"
                          label={projects[projectId].name}
                          path={`/t/${companyId}/project/${projectId}/task`}
                        />
                      ))}
                  </SubMenu>
                );
              })}
          </Menu>
          <LastMenuItemContainer key="basic-config">
            {(companyAdmin || isAdminInAnyProject) && (
              <Menu.Item
                iconType="setting.svg"
                key="company"
                label={t('manage')}
                exact={false}
                path={`/t/${companyId}/admin/`}
                toPath={`/t/${companyId}/admin/members`}
              />
            )}
            {companyName && thisUserId && (
              <Menu.Item
                iconType="chats.svg"
                key="direct"
                label={t('directs')}
                path={`/t/${companyId}/messages`}
                unreadMessage={
                  unreadChatMessage ? t('unreadMessage') : undefined
                }
              />
            )}
          </LastMenuItemContainer>
        </div>
      ) : (
        <Menu>
          <Menu.Item
            iconType="home.svg"
            key="create-company"
            label={t('home')}
            path="/homepage"
          />
        </Menu>
      )}
    </SideBar>
  );
};

export default TaskSidebar;
